import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Platform,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { pickTourDayCard } from '../../utils/calendarDayCard';
import { parseScheduleText, ScheduleEditor } from '../ScheduleEditor';
import {
  SCHEDULE_CUES,
  getScheduleCue,
  upsertScheduleCue,
  seedScheduleCues,
} from '../../utils/scheduleCues';
import { formatTimeInputMask } from '../../utils/scheduleTimeUtils';
import { applyPickerColor, selectAccentSwatch, normalizeAccentSwatches, hexWithAlpha, normalizeLaneColors, normalizeChatColors, glossyFill, glossyWebStyle } from '../../utils/calendarV2Prefs';
import { buildRiderLibrary, riderLibraryColor, RIDER_BAND_COLORS, RIDER_VEHICLE_COLOR, RIDER_VEHICLE_ID, normalizeTourActs } from '../../utils/calendarRiderLibrary';
import {
  dateKeysForDayStrip,
  tourTimelineDateKeys,
  timelineColumnWidth,
  WEEKDAY_LABELS,
  toDateKey,
  DAY_STRIP_CELL,
  DAY_STRIP_GAP,
  DAY_STRIP_MONTH_RULE,
  DAY_STRIP_ROW_HEIGHT,
  DAY_STRIP_WRAP_PAD_Y,
  DAY_STRIP_PEEK_DAYS,
  dayStripShowsMonthRule,
  dayStripMonthStackLabel,
  dayStripItemX,
  dayStripPeekIndex,
  dayStripTodayOffscreen,
  dayStripIndexAtX,
  formatClockTime,
} from '../../utils/calendarHelpers';
import {
  parseChatState,
  serializeChatState,
  emptyChatChannels,
  CHAT_CHANNEL_META,
  membersForChannel,
  toggleChatMember,
  isMemberOnChannel,
  chatMemberKey,
  markChannelRead,
  mergeChatReads,
  preferLongerChatChannels,
  isOwnChatMessage,
  chatMemberForMessage,
  readersForMessage,
  collectChatMessageIds,
  shouldPlayChatChime,
} from '../../utils/calendarV2Persist';
import { getUserPhotoSource } from '../../utils/userPhoto';
import { displayNameFromUserLike, personInitials } from '../../utils/personInitials';
import { MasterTourRoutingMap } from './MasterTourRoutingMap';
import { TourPlanner } from '../../screens/TourPlanner';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import RiderEmailRecipientsModal from './RiderEmailRecipientsModal';
import { sendRiderToEmails, getFirestoreDb } from '../../utils/riderShareService';
import { upsertDayShareLink, SHARE_VISIBILITY } from '../../utils/calendarShareService';
import { formatDateLong } from '../../utils/calendarContacts';
import {
  addLineToCategory,
  categoriesForArea,
  parseRiderAreas,
  serializeRiderAreas,
  withAreaCategories,
} from '../../utils/riderAreas';
import {
  RIDER_PACKAGE_SECTIONS,
  DEFAULT_RIDER_PACKAGE,
  shareModulesForRiderPackage,
  riderPackageMessage,
  resolveWebBaseUrl,
} from '../../utils/masterTourSend';

function cueTime(rows, needles) {
  const hit = (rows || []).find((s) => {
    const label = String(s.label || '').toLowerCase();
    return needles.some((n) => label.includes(n));
  });
  return hit?.t && hit.t !== '—' ? hit.t : '—';
}

function scheduleRowsFromText(raw) {
  const { lines } = parseScheduleText(String(raw || ''));
  return (lines || [])
    .filter((s) => s && s.kind !== 'blank' && s.kind !== 'unset')
    .map((s) => ({
      t: s.kind === 'schedule' ? s.time : '—',
      label: s.kind === 'schedule' ? (s.item || s.notes || '') : (s.text || ''),
      who: s.kind === 'schedule' ? String(s.notes || '').trim() : '',
      key: s.kind === 'schedule',
    }))
    .filter((s) => s.label);
}

function initialsOf(name, fallback) {
  return personInitials(name, fallback);
}

/** Top pill row — Claude offline HTML. */
export const CLAUDE_VIEW_TABS = [
  { id: 'map', label: 'Map' },
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'files', label: 'Files' },
  { id: 'tourparty', label: 'Tour Party' },
  { id: 'chat', label: 'Chat' },
];

/** Second nav row — Claude offline HTML. */
export const CLAUDE_NAV_TABS = [
  { id: 'riders', label: 'Rider' },
  { id: 'setlist', label: 'Setlist' },
  { id: 'guestlist', label: 'Guest List' },
  { id: 'day', label: 'Schedule' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'merch', label: 'Merch' },
  { id: 'settle', label: 'Settlement' },
  { id: 'lodging', label: 'Lodging' },
  { id: 'daysheet', label: 'Day Sheet' },
  { id: 'notes', label: 'Notes' },
];

export const CLAUDE_CANVAS_IDS = new Set([
  ...CLAUDE_VIEW_TABS.map((t) => t.id),
  ...CLAUDE_NAV_TABS.map((t) => t.id),
]);

export const VRC_EXTRA_TABS = ['STAGE', 'NOTES'];

function inkOnHex(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return '#14141a';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return '#14141a';
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.58 ? '#14141a' : '#fffffe';
}

let colorPickerHeld = false;
let colorPickerUntil = 0;

/** True while the OS color well is open, plus a short grace after it closes (ghost clicks). */
export function isNativeColorPickerActive() {
  return colorPickerHeld || Date.now() < colorPickerUntil;
}

function holdColorPicker() {
  colorPickerHeld = true;
  colorPickerUntil = Date.now() + 800;
}

function releaseColorPicker() {
  colorPickerHeld = false;
  colorPickerUntil = Date.now() + 800;
}

/** Uncontrolled while picking so React value updates do not dismiss the native color well. */
function WebColorInput({
  value,
  onChange,
  pal,
  ariaLabel,
  title,
  width = 36,
  height = 28,
}) {
  const ref = useRef(null);
  const hex = /^#[0-9a-fA-F]{6}$/.test(String(value || '')) ? String(value).toLowerCase() : '#2a7d5c';
  useEffect(() => {
    if (isNativeColorPickerActive()) return undefined;
    if (ref.current && ref.current.value !== hex) ref.current.value = hex;
    return undefined;
  }, [hex]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof el.addEventListener !== 'function') return undefined;
    const onClosed = () => releaseColorPicker();
    el.addEventListener('change', onClosed);
    return () => el.removeEventListener('change', onClosed);
  }, []);
  if (Platform.OS !== 'web') {
    return (
      <View
        style={{
          width,
          height,
          borderRadius: 8,
          backgroundColor: hex,
          borderWidth: 1,
          borderColor: pal.line,
        }}
      />
    );
  }
  return (
    <input
      ref={ref}
      type="color"
      defaultValue={hex}
      aria-label={ariaLabel}
      title={title}
      onClick={(ev) => ev.stopPropagation()}
      onPointerDown={(ev) => {
        ev.stopPropagation();
        holdColorPicker();
      }}
      onInput={(ev) => {
        holdColorPicker();
        onChange(ev.target.value);
      }}
      style={{
        width,
        height,
        padding: 0,
        border: `1px solid ${pal.line}`,
        borderRadius: 8,
        background: pal.surf || pal.page,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    />
  );
}

function WebColorField({ label, value, onChange, pal }) {
  const hex = String(value || '#3aa87a');
  return (
    <View style={barStyles.colorRow}>
      <WebColorInput value={hex} onChange={onChange} pal={pal} ariaLabel={label} title={label} />
      <Text style={[barStyles.colorLabel, { color: pal.ink }]}>{label}</Text>
      <Text style={[barStyles.colorHex, { color: pal.mute }]}>{hex}</Text>
    </View>
  );
}

function DesignToggle({ id, label, pal, prefs, onChange }) {
  const on = Boolean(prefs[id]);
  return (
    <TouchableOpacity
      onPress={() => onChange({ [id]: !on })}
      style={[barStyles.toggle, { borderColor: on ? pal.teal : pal.line, backgroundColor: on ? pal.teal : pal.surf }]}
      accessibilityLabel={label}
    >
      <Text style={[barStyles.toggleLabel, { color: on ? '#fffffe' : pal.ink }]}>{label}</Text>
    </TouchableOpacity>
  );
}

function DesignSelect({ id, options, pal, prefs, onChange }) {
  return (
    <View style={[barStyles.select, { borderColor: pal.line, backgroundColor: pal.page }]}>
      {options.map((opt) => (
        <TouchableOpacity
          key={opt.value}
          onPress={() => onChange({ [id]: opt.value })}
          style={[barStyles.selectOpt, prefs[id] === opt.value && { backgroundColor: pal.ink }]}
        >
          <Text style={[barStyles.selectText, { color: prefs[id] === opt.value ? pal.page : pal.ink }]}>
            {opt.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function DesignSection({ title, pal, children }) {
  return (
    <View style={barStyles.section}>
      <Text style={[barStyles.sectionTitle, { color: pal.ink }]}>{title}</Text>
      {children}
    </View>
  );
}

export function MasterTourDesignBar({ prefs, onChange, onReset, onSave, isDarkMode, sheet = false }) {
  const pal = palette(isDarkMode);
  const lanes = normalizeLaneColors(prefs.laneColors, prefs.accent);
  const chat = normalizeChatColors(prefs.chatColors);
  return (
    <View style={[barStyles.wrap, sheet && barStyles.wrapSheet, { backgroundColor: pal.surf, borderBottomColor: pal.line, borderBottomWidth: sheet ? 0 : 1 }]}>
      <DesignSection title="Layout" pal={pal}>
        <View style={barStyles.field}>
          <Text style={[barStyles.kicker, { color: pal.mute }]}>Density</Text>
          <DesignSelect
            id="density"
            pal={pal}
            prefs={prefs}
            onChange={onChange}
            options={[
              { value: 'comfortable', label: 'Comfortable' },
              { value: 'compact', label: 'Compact' },
            ]}
          />
        </View>
        <View style={barStyles.field}>
          <Text style={[barStyles.kicker, { color: pal.mute }]}>Week starts</Text>
          <DesignSelect
            id="weekStart"
            pal={pal}
            prefs={prefs}
            onChange={onChange}
            options={[
              { value: 'sunday', label: 'Sunday' },
              { value: 'monday', label: 'Monday' },
            ]}
          />
        </View>
        <View style={barStyles.field}>
          <Text style={[barStyles.kicker, { color: pal.mute }]}>Time format</Text>
          <DesignSelect
            id="timeFormat"
            pal={pal}
            prefs={prefs}
            onChange={onChange}
            options={[
              { value: '12-hour', label: '12-hour' },
              { value: '24-hour', label: '24-hour' },
            ]}
          />
        </View>
      </DesignSection>
      <DesignSection title="Day details" pal={pal}>
        <View style={barStyles.toggleRow}>
          <DesignToggle id="showTravelDetail" label="Travel detail" pal={pal} prefs={prefs} onChange={onChange} />
          <DesignToggle id="liveFlightStatus" label="Live flight status" pal={pal} prefs={prefs} onChange={onChange} />
        </View>
      </DesignSection>
      <DesignSection title="Accent" pal={pal}>
        <View style={barStyles.swatches}>
          {normalizeAccentSwatches(prefs.accents).map((c, i) => {
            const on = Number(prefs.accentSlot) === i || String(prefs.accent).toLowerCase() === c;
            return (
              <TouchableOpacity
                key={`swatch-${i}`}
                onPress={() => onChange(selectAccentSwatch(prefs, i))}
                style={[barStyles.swatchLg, { backgroundColor: c, borderColor: on ? pal.ink : pal.line }]}
                accessibilityLabel={`Accent ${c}`}
              >
                {on ? <Text style={barStyles.check}>✓</Text> : null}
              </TouchableOpacity>
            );
          })}
          <WebColorInput
            value={String(prefs.accent || '#2a7d5c')}
            onChange={(hex) => onChange(applyPickerColor(prefs, hex))}
            pal={{ ...pal, surf: pal.page }}
            ariaLabel="Pick accent color"
            title="Recolor the selected swatch"
          />
        </View>
      </DesignSection>
      <DesignSection title="Timeline colors" pal={pal}>
        <WebColorField label="Shows" value={lanes.show} pal={pal} onChange={(hex) => onChange({ laneColors: { ...lanes, show: hex } })} />
        <WebColorField label="Travel" value={lanes.travel} pal={pal} onChange={(hex) => onChange({ laneColors: { ...lanes, travel: hex } })} />
        <WebColorField label="Days off" value={lanes.off} pal={pal} onChange={(hex) => onChange({ laneColors: { ...lanes, off: hex } })} />
        <WebColorField label="Openers" value={lanes.opener} pal={pal} onChange={(hex) => onChange({ laneColors: { ...lanes, opener: hex } })} />
        <WebColorField label="Personnel" value={lanes.personnel} pal={pal} onChange={(hex) => onChange({ laneColors: { ...lanes, personnel: hex } })} />
      </DesignSection>
      <DesignSection title="Chat colors" pal={pal}>
        <WebColorField label="Accent" value={chat.accent} pal={pal} onChange={(hex) => onChange({ chatColors: { ...chat, accent: hex } })} />
        <WebColorField label="Bubbles" value={chat.bubble} pal={pal} onChange={(hex) => onChange({ chatColors: { ...chat, bubble: hex } })} />
        <WebColorField label="Your messages" value={chat.mine} pal={pal} onChange={(hex) => onChange({ chatColors: { ...chat, mine: hex } })} />
        <WebColorField label="Active channel" value={chat.channel} pal={pal} onChange={(hex) => onChange({ chatColors: { ...chat, channel: hex } })} />
      </DesignSection>
      <View style={barStyles.sheetActions}>
        <TouchableOpacity onPress={onSave} style={[barStyles.btnPrimary, { backgroundColor: pal.ink, borderColor: pal.ink }]}>
          <Text style={[barStyles.btnText, { color: pal.page }]}>Save as defaults</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onReset} style={[barStyles.btn, { borderColor: pal.line }]}>
          <Text style={[barStyles.btnText, { color: pal.ink }]}>Reset</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEK_BAR_JUMP_DAYS = 7;

/** Persistent day strip under Claude nav. Full calendar around the cursor; show days stay marked. */
export function MasterTourWeekBar({
  cursorDate,
  selectedDateKey,
  weekStartsOn = 0,
  dateKeys,
  tourDayMetaByDate,
  selectedTourFilterId,
  isDarkMode,
  accent,
  laneColors,
  onDayPress,
  onBackToToday,
  showTodayButton = true,
  scrollToTodayToken = 0,
  flush = false,
}) {
  const pal = palette(isDarkMode);
  const lanes = normalizeLaneColors(laneColors, accent || pal.teal);
  const scrollRef = useRef(null);
  const xRef = useRef(0);
  const maxXRef = useRef(0);
  const viewportW = useRef(0);
  const contentW = useRef(0);
  const keysRef = useRef([]);
  const pendingAlignRef = useRef(false);
  const lastSelectedKeyRef = useRef(null);
  const xByKeyRef = useRef({});
  const pendingScrollKeyRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [todayOffscreen, setTodayOffscreen] = useState(false);
  const todayKey = toDateKey(new Date());
  const keys = useMemo(
    () => dateKeysForDayStrip({ tourDateKeys: dateKeys, cursorDate, weekStartsOn }),
    [dateKeys, cursorDate, weekStartsOn]
  );
  keysRef.current = keys;
  const days = useMemo(() => keys.map((dateKey, index) => {
    const parts = String(dateKey).split('-').map(Number);
    const d = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : new Date();
    const card = pickTourDayCard(tourDayMetaByDate?.[dateKey], selectedTourFilterId);
    return {
      dateKey,
      dow: WEEKDAY_LABELS[d.getDay()] || '',
      num: d.getDate(),
      city: card?.cityLine || '',
      kind: card?.kindLabel || '',
      kindKey: card?.kind || '',
      month: d.getDate() === 1 ? (MONTH_SHORT[d.getMonth()] || '') : '',
      monthRule: dayStripShowsMonthRule(keys, index),
      monthStack: dayStripShowsMonthRule(keys, index) ? dayStripMonthStackLabel(dateKey) : '',
    };
  }), [keys, tourDayMetaByDate, selectedTourFilterId]);

  const updateArrows = (x, maxX) => {
    xRef.current = x;
    maxXRef.current = Math.max(0, maxX);
    setCanPrev(x > 4);
    setCanNext(x < maxX - 4);
    const todayX = xByKeyRef.current[todayKey];
    const vw = viewportW.current || 0;
    const off = typeof todayX === 'number'
      ? todayX + DAY_STRIP_CELL <= x + 4 || todayX >= x + vw - 4
      : dayStripTodayOffscreen(keysRef.current, todayKey, x, vw);
    setTodayOffscreen((prev) => (prev === off ? prev : off));
  };

  const applyScrollX = (x) => {
    const next = Math.max(0, Math.min(maxXRef.current || x, x));
    scrollRef.current?.scrollTo({ x: next, y: 0, animated: false });
    if (Platform.OS === 'web') {
      const node = scrollRef.current?.getScrollableNode?.() || scrollRef.current;
      const el = node?.scrollTo ? node : node?._scrollNode;
      if (el && typeof el.scrollLeft === 'number') el.scrollLeft = next;
    }
  };

  const scrollToDate = (dateKey) => {
    if (!dateKey || !scrollRef.current) return false;
    const list = keysRef.current;
    const peekKey = list[dayStripPeekIndex(list, dateKey, DAY_STRIP_PEEK_DAYS)];
    if (!peekKey) return false;
    const x = xByKeyRef.current[peekKey];
    if (typeof x !== 'number') {
      pendingScrollKeyRef.current = dateKey;
      return false;
    }
    pendingScrollKeyRef.current = null;
    applyScrollX(x);
    return true;
  };

  const rememberCellX = (dateKey, x) => {
    xByKeyRef.current[dateKey] = x;
    const pending = pendingScrollKeyRef.current;
    if (pending) scrollToDate(pending);
  };

  const scrollBy = (dir) => {
    const cell = DAY_STRIP_CELL + DAY_STRIP_GAP;
    const visible = viewportW.current > 0 ? Math.floor(viewportW.current / cell) : WEEK_BAR_JUMP_DAYS;
    const jump = Math.max(WEEK_BAR_JUMP_DAYS, visible);
    const from = dayStripIndexAtX(keysRef.current, xRef.current);
    const nextI = Math.max(0, Math.min(keysRef.current.length - 1, from + dir * jump));
    const peekKey = keysRef.current[nextI];
    const measured = peekKey != null ? xByKeyRef.current[peekKey] : undefined;
    const next = typeof measured === 'number' ? measured : dayStripItemX(keysRef.current, nextI);
    applyScrollX(Math.min(maxXRef.current || next, next));
  };

  const handleBackToToday = () => {
    pendingAlignRef.current = true;
    pendingScrollKeyRef.current = todayKey;
    if (onBackToToday) onBackToToday();
    else onDayPress?.(todayKey);
    scrollToDate(todayKey);
  };

  useEffect(() => {
    const force = pendingAlignRef.current;
    pendingAlignRef.current = false;
    const selChanged = lastSelectedKeyRef.current !== selectedDateKey;
    lastSelectedKeyRef.current = selectedDateKey;
    if (!selectedDateKey) return;
    if (!selChanged && !force) return;
    pendingScrollKeyRef.current = selectedDateKey;
    scrollToDate(selectedDateKey);
  }, [selectedDateKey, keys, todayKey]);

  useEffect(() => {
    if (!scrollToTodayToken) return;
    pendingAlignRef.current = true;
    pendingScrollKeyRef.current = todayKey;
    scrollToDate(todayKey);
  }, [scrollToTodayToken, todayKey]);

  return (
    <View
      style={[
        weekBarStyles.wrap,
        flush && weekBarStyles.wrapFlush,
        { backgroundColor: pal.surf, borderBottomColor: pal.line },
      ]}
    >
      <TouchableOpacity
        onPress={() => scrollBy(-1)}
        disabled={!canPrev}
        style={[weekBarStyles.arrow, { borderColor: pal.line, opacity: canPrev ? 1 : 0.35 }]}
        accessibilityLabel="Previous days"
      >
        <Text style={[weekBarStyles.arrowGlyph, { color: pal.ink }]}>‹</Text>
      </TouchableOpacity>
      <ScrollView
        ref={scrollRef}
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={weekBarStyles.scroller}
        {...(Platform.OS === 'web' ? { className: 'v2-day-strip-scroll' } : {})}
        onScroll={(e) => {
          const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
          viewportW.current = layoutMeasurement.width;
          contentW.current = contentSize.width;
          updateArrows(contentOffset.x, contentSize.width - layoutMeasurement.width);
        }}
        onLayout={(e) => {
          viewportW.current = e.nativeEvent.layout.width;
          updateArrows(xRef.current, Math.max(0, contentW.current - viewportW.current));
          if (pendingScrollKeyRef.current) scrollToDate(pendingScrollKeyRef.current);
        }}
        onContentSizeChange={(w) => {
          contentW.current = w;
          updateArrows(xRef.current, Math.max(0, w - viewportW.current));
          if (pendingScrollKeyRef.current) scrollToDate(pendingScrollKeyRef.current);
        }}
        scrollEventThrottle={16}
        style={weekBarStyles.scroll}
      >
        {days.map((day) => {
          const sel = day.dateKey === selectedDateKey;
          const laneHex = day.kindKey && lanes[day.kindKey] ? lanes[day.kindKey] : null;
          const glossHex = laneHex || (sel ? (accent || pal.teal) : null);
          const gloss = glossHex ? glossyFill(glossHex, { isDarkMode, selected: sel, role: 'chip' }) : null;
          const ink = gloss?.ink || pal.ink;
          const mute = gloss?.mute || pal.mute;
          return (
            <React.Fragment key={day.dateKey}>
              {day.monthRule ? (
                <View
                  style={weekBarStyles.monthRule}
                  accessibilityLabel={`Start of ${day.monthStack || day.month || 'month'}`}
                >
                  <View style={weekBarStyles.monthRuleLetters}>
                    {[...String(day.monthStack || '')].map((ch, i) => (
                      <Text key={`${day.dateKey}-m-${i}`} style={[weekBarStyles.monthRuleGlyph, { color: pal.ink }]}>
                        {ch}
                      </Text>
                    ))}
                  </View>
                  <View style={[weekBarStyles.monthRuleLine, { backgroundColor: pal.ink }]} />
                </View>
              ) : null}
              <TouchableOpacity
                onPress={() => onDayPress?.(day.dateKey)}
                onLayout={(e) => rememberCellX(day.dateKey, e.nativeEvent.layout.x)}
                style={[
                  weekBarStyles.cell,
                  sel ? weekBarStyles.cellSelected : weekBarStyles.cellIdle,
                  {
                    borderWidth: sel ? 3 : 1,
                    borderLeftWidth: sel ? 3 : (glossHex ? 3 : 1),
                    borderLeftColor: sel ? pal.ink : (glossHex || pal.line),
                    overflow: sel ? 'visible' : 'hidden',
                    ...(gloss
                      ? (Platform.OS === 'web'
                        ? { ...glossyWebStyle(gloss), borderColor: sel ? pal.ink : gloss.borderColor }
                        : { backgroundColor: gloss.backgroundColor, borderColor: sel ? pal.ink : gloss.borderColor })
                      : {
                        borderColor: sel ? pal.ink : pal.line,
                        backgroundColor: pal.surf,
                      }),
                    ...(sel && Platform.OS === 'web'
                      ? { boxShadow: isDarkMode ? '0 0 0 2px rgba(255,255,255,0.5)' : '0 0 0 2px rgba(20,20,26,0.28)' }
                      : null),
                  },
                ]}
                accessibilityState={{ selected: sel }}
                accessibilityLabel={`${day.dow} ${day.num}${day.month ? ` ${day.month}` : ''}${day.kind ? ` ${day.kind}` : ''}`}
              >
                <View style={weekBarStyles.cellTop}>
                  <Text style={[weekBarStyles.dow, { color: mute }]}>{day.dow}</Text>
                  <Text style={[weekBarStyles.num, { color: ink, fontWeight: sel ? '700' : '400' }]}>{day.num}</Text>
                  {day.month ? (
                    <Text style={[weekBarStyles.dow, { color: ink }]}>{day.month}</Text>
                  ) : null}
                </View>
                <Text style={[weekBarStyles.city, { color: ink }]} numberOfLines={1}>
                  {day.city || '—'}
                </Text>
                {day.kind ? (
                  <Text style={[weekBarStyles.kind, { color: mute }]} numberOfLines={1}>
                    {day.kind}
                  </Text>
                ) : null}
              </TouchableOpacity>
            </React.Fragment>
          );
        })}
      </ScrollView>
      <TouchableOpacity
        onPress={() => scrollBy(1)}
        disabled={!canNext}
        style={[weekBarStyles.arrow, { borderColor: pal.line, opacity: canNext ? 1 : 0.35 }]}
        accessibilityLabel="Next days"
      >
        <Text style={[weekBarStyles.arrowGlyph, { color: pal.ink }]}>›</Text>
      </TouchableOpacity>
      {showTodayButton ? (
      <TouchableOpacity
        onPress={handleBackToToday}
        style={[
          weekBarStyles.todayBtn,
          {
            borderColor: todayOffscreen ? pal.ink : pal.line,
            backgroundColor: todayOffscreen ? pal.ink : pal.surf,
          },
        ]}
        accessibilityLabel="Back to today"
      >
        <Text style={[weekBarStyles.todayBtnText, { color: todayOffscreen ? pal.page : pal.ink }]}>
          Back to Today
        </Text>
      </TouchableOpacity>
      ) : null}
    </View>
  );
}

export function MasterTourTimeline({
  year,
  month,
  tourDayMetaByDate,
  selectedTourFilterId,
  selectedDateKey,
  onDayPress,
  isDarkMode,
  accent,
  laneColors,
  tour,
  partyMembers,
}) {
  const pal = palette(isDarkMode);
  const lanes = normalizeLaneColors(laneColors, accent || pal.teal);
  const keys = useMemo(
    () => tourTimelineDateKeys({ tourDayMetaByDate, selectedTourFilterId, year, month }),
    [tourDayMetaByDate, selectedTourFilterId, year, month]
  );
  const [trackWidth, setTrackWidth] = useState(0);
  const LABEL_W = 132;
  const COL = timelineColumnWidth(keys.length, Math.max(0, trackWidth - 48), LABEL_W);
  const ROW_H = 48;
  const openerActs = useMemo(
    () => normalizeTourActs(tour, {}).filter((a) => /opener/i.test(String(a.role || ''))),
    [tour]
  );
  const rows = useMemo(() => {
    const kinds = { show: [], travel: [], off: [], opener: [], personnel: [] };
    keys.forEach((key, index) => {
      const card = pickTourDayCard(tourDayMetaByDate?.[key], selectedTourFilterId);
      if (card?.kind && kinds[card.kind]) kinds[card.kind].push(index);
      if (card?.kind === 'show' && openerActs.length) kinds.opener.push(index);
      if (card?.kind === 'show' || card?.kind === 'travel') kinds.personnel.push(index);
    });
    const toBars = (indexes, bg) => {
      if (!indexes.length) return [];
      const bars = [];
      let start = indexes[0];
      let prev = indexes[0];
      const flush = () => bars.push({ start, span: prev - start + 1, bg });
      for (let i = 1; i < indexes.length; i++) {
        if (indexes[i] === prev + 1) prev = indexes[i];
        else {
          flush();
          start = indexes[i];
          prev = indexes[i];
        }
      }
      flush();
      return bars;
    };
    const out = [
      { id: 'show', label: 'Shows', bars: toBars(kinds.show, lanes.show) },
      { id: 'travel', label: 'Travel days', bars: toBars(kinds.travel, lanes.travel) },
      { id: 'off', label: 'Days off', bars: toBars(kinds.off, lanes.off) },
      { id: 'opener', label: openerActs.length ? `Openers (${openerActs.length})` : 'Openers', bars: toBars(kinds.opener, lanes.opener) },
      { id: 'personnel', label: partyMembers?.length ? `Personnel (${partyMembers.length})` : 'Personnel', bars: toBars(kinds.personnel, lanes.personnel) },
    ];
    return out;
  }, [keys, tourDayMetaByDate, selectedTourFilterId, lanes.show, lanes.travel, lanes.off, lanes.opener, lanes.personnel, openerActs, partyMembers]);

  const rangeLabel = (() => {
    if (!keys.length) return '';
    const a = keys[0];
    const b = keys[keys.length - 1];
    const fmt = (key) => {
      const [y, m] = key.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
    };
    const left = fmt(a);
    const right = fmt(b);
    return left === right ? left : `${left} – ${right}`;
  })();

  return (
    <View
      style={{ flex: 1, minHeight: 0, backgroundColor: pal.page }}
      onLayout={(e) => {
        const next = Math.round(e.nativeEvent.layout.width || 0);
        if (next > 0) setTrackWidth((prev) => (prev === next ? prev : next));
      }}
    >
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 24, minHeight: '100%', flexGrow: 1 }} horizontal={false}>
        <Text style={[styles.label, { color: pal.ink, marginBottom: 14 }]}>{rangeLabel || 'Timeline'}</Text>
        <ScrollView horizontal contentContainerStyle={{ minWidth: LABEL_W + keys.length * COL + 24, paddingBottom: 12 }}>
          <View>
            <View style={{ flexDirection: 'row', gap: 14, marginBottom: 10, alignItems: 'flex-end' }}>
              <Text style={[styles.label, { color: pal.mute, width: LABEL_W }]}>Date</Text>
              <View style={{ flexDirection: 'row' }}>
                {keys.map((key) => {
                  const parts = key.split('-').map(Number);
                  const d = new Date(parts[0], parts[1] - 1, parts[2]);
                  const sel = key === selectedDateKey;
                  const monthStart = parts[2] === 1;
                  return (
                    <TouchableOpacity
                      key={key}
                      onPress={() => onDayPress?.(key)}
                      style={{ width: COL, alignItems: 'center', gap: 2 }}
                    >
                      {monthStart ? (
                        <Text style={[styles.tick, { color: pal.ink, fontWeight: '700' }]}>
                          {MONTH_SHORT[parts[1] - 1]}
                        </Text>
                      ) : (
                        <Text style={[styles.tick, { color: pal.mute }]}>{WEEKDAY_LABELS[d.getDay()]?.[0]}</Text>
                      )}
                      <Text style={[styles.tick, { color: sel ? pal.ink : pal.inkSoft, fontWeight: sel ? '700' : '500', fontSize: 12 }]}>
                        {parts[2]}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            {rows.map((row) => (
              <View key={row.id} style={{ flexDirection: 'row', gap: 14, alignItems: 'center', marginBottom: 10 }}>
                <Text style={[styles.label, { color: pal.ink, width: LABEL_W }]}>{row.label}</Text>
                <View style={[styles.ganttTrack, { backgroundColor: pal.sunk, borderColor: pal.line, width: keys.length * COL, height: ROW_H }]}>
                  {row.bars.map((b, i) => (
                    <View
                      key={`${row.id}-${i}`}
                      style={{
                        position: 'absolute',
                        left: b.start * COL + 3,
                        width: Math.max(8, b.span * COL - 6),
                        top: 8,
                        height: ROW_H - 16,
                        borderRadius: 8,
                        backgroundColor: b.bg,
                      }}
                    />
                  ))}
                </View>
              </View>
            ))}
            <View style={{ flexDirection: 'row', gap: 22, marginTop: 18, paddingLeft: LABEL_W + 14 }}>
              {[
                { label: 'Show', bg: lanes.show },
                { label: 'Travel', bg: lanes.travel },
                { label: 'Off', bg: lanes.off },
              ].map((l) => (
                <View key={l.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ width: 18, height: 10, borderRadius: 3, backgroundColor: l.bg }} />
                  <Text style={[styles.meta, { color: pal.ink }]}>{l.label}</Text>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </ScrollView>
    </View>
  );
}

function ScheduleCueHeader({
  scheduleText,
  pal,
  onPersistSchedule,
  timeFormat,
}) {
  const editable = Boolean(onPersistSchedule);
  return (
    <View style={{ flexDirection: 'row', gap: 14, flexWrap: 'wrap' }}>
      {SCHEDULE_CUES.map((cue) => {
        const value = getScheduleCue(scheduleText, cue.id);
        const display = timeFormat === '24-hour' && value.time
          ? value.time
          : (value.time ? `${value.time} ${value.period}` : '');
        return (
          <View key={cue.id} style={{ minWidth: 108 }}>
            <Text style={[styles.label, { color: pal.faint, marginBottom: 4 }]}>{cue.label}</Text>
            {editable ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <TextInput
                  value={value.time}
                  onChangeText={(t) => {
                    onPersistSchedule(upsertScheduleCue(scheduleText, cue.id, {
                      time: formatTimeInputMask(t),
                      period: value.period,
                    }));
                  }}
                  placeholder="—"
                  placeholderTextColor={pal.faint}
                  keyboardType="numbers-and-punctuation"
                  accessibilityLabel={`${cue.label} time`}
                  style={{
                    minWidth: 64,
                    fontSize: 17,
                    fontWeight: '500',
                    color: pal.ink,
                    fontFamily: FONT.mono,
                    paddingVertical: 2,
                    borderBottomWidth: 1,
                    borderBottomColor: pal.line,
                  }}
                />
                <TouchableOpacity
                  onPress={() => {
                    onPersistSchedule(upsertScheduleCue(scheduleText, cue.id, {
                      time: value.time,
                      period: value.period === 'AM' ? 'PM' : 'AM',
                    }));
                  }}
                  accessibilityLabel={`${cue.label} ${value.period}`}
                  hitSlop={6}
                >
                  <Text style={{ fontSize: 12, fontWeight: '700', color: pal.mute, fontFamily: FONT.mono }}>
                    {value.period}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <Text style={{ fontSize: 17, fontWeight: '500', color: pal.ink, fontFamily: FONT.mono }}>
                {display || '—'}
              </Text>
            )}
          </View>
        );
      })}
    </View>
  );
}

export function MasterTourDaySheet({
  dateKey,
  tourDayMetaByDate,
  selectedTourFilterId,
  scheduleText,
  lodgingSummary,
  groundLine,
  notesText,
  guestCount,
  partyMembers,
  prefs,
  isDarkMode,
  accent,
  theme,
  onPersistSchedule,
  onPersistNotes,
  busCallTime = '',
  travelTimeLabel = '',
  notesDiverge = false,
}) {
  const pal = palette(isDarkMode);
  const card = pickTourDayCard(tourDayMetaByDate?.[dateKey], selectedTourFilterId);
  const d = dateKey ? new Date(`${dateKey}T12:00:00`) : new Date();
  const stamp = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const rows = scheduleRowsFromText(scheduleText);
  const members = partyMembers || [];
  const notesBoot = useRef('');
  const seedKey = useRef('');
  const [notesDraft, setNotesDraft] = useState(notesText || '');
  useEffect(() => {
    notesBoot.current = '';
    setNotesDraft(notesText || '');
  }, [dateKey]);
  useEffect(() => {
    if (notesBoot.current === dateKey) return;
    if (String(notesText || '').length) {
      notesBoot.current = dateKey;
      setNotesDraft(notesText);
    }
  }, [dateKey, notesText]);
  useEffect(() => {
    if (!onPersistSchedule || !dateKey) return;
    const key = `${dateKey}|${busCallTime || ''}`;
    if (seedKey.current === key) return;
    const seeded = seedScheduleCues(scheduleText || '', { busCallTime });
    seedKey.current = key;
    if (seeded !== String(scheduleText || '')) onPersistSchedule(seeded);
  }, [dateKey, busCallTime, onPersistSchedule, scheduleText]);
  return (
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, maxWidth: 1280 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14, marginBottom: 22, flexWrap: 'wrap', gap: 16 }}>
        <View style={{ flex: 1, paddingRight: 16, minWidth: 220 }}>
          <Text style={[styles.label, { color: pal.mute, marginBottom: 6 }]}>{stamp} · {(card?.kindLabel || 'Day').toUpperCase()}</Text>
          <Text style={[styles.hero, { color: pal.ink }]}>{card?.cityLine || '—'}</Text>
          <Text style={{ fontSize: 15, color: pal.inkSoft, marginTop: 6 }}>{card?.venueLine || '—'}</Text>
        </View>
        <ScheduleCueHeader
          scheduleText={scheduleText}
          pal={pal}
          onPersistSchedule={onPersistSchedule}
          timeFormat={prefs?.timeFormat || '12-hour'}
        />
      </View>
      <View style={{ flexDirection: 'row', gap: 26, flexWrap: 'wrap' }}>
        <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line, flex: 1, minWidth: 280 }]}>
          <Text style={[styles.label, { color: pal.mute, marginBottom: 12 }]}>Day schedule</Text>
          {onPersistSchedule && theme ? (
            <ScheduleEditor
              value={scheduleText || ''}
              onChange={(next) => onPersistSchedule(next)}
              mode="full"
              theme={theme}
              fullUnscroll
            />
          ) : rows.length ? rows.map((s, i) => (
            <View key={`${s.t}-${i}`} style={[styles.schedRow, { borderTopColor: pal.lineSoft }]}>
              <Text style={[styles.mono, { width: 72, color: pal.ink }]}>{fmt(s.t)}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ color: pal.ink, fontWeight: s.key ? '600' : '400' }}>{s.label}</Text>
                {s.who ? <Text style={{ color: pal.mute, fontSize: 14.5, marginTop: 1 }}>{s.who}</Text> : null}
              </View>
            </View>
          )) : (
            <Text style={{ color: pal.mute }}>No schedule for this day yet.</Text>
          )}
        </View>
        <View style={{ flex: 1, minWidth: 280, gap: 16 }}>
          <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
              <Text style={[styles.label, { color: pal.mute }]}>Flights</Text>
              <Text style={[styles.meta, { color: pal.faint }]}>
                {prefs?.liveFlightStatus ? 'FlightAware · scheduled legs' : 'Sync paused'}
              </Text>
            </View>
            <Text style={{ color: pal.ink, fontSize: 15 }}>
              {prefs?.showTravelDetail ? (card?.travelLine || 'No air movement. Party travels by bus overnight.') : 'Travel detail hidden.'}
            </Text>
          </View>
          <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line, flexDirection: 'row', gap: 18 }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.label, { color: pal.mute, marginBottom: 7 }]}>Hotel</Text>
              <Text style={{ color: pal.ink, fontSize: 15, fontWeight: '500' }}>{lodgingSummary || (card?.hasHotel ? 'Rooms held' : '—')}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.label, { color: pal.mute, marginBottom: 7 }]}>Ground</Text>
              <Text style={{ color: pal.ink, fontSize: 15, fontWeight: '500' }}>{groundLine || '—'}</Text>
            </View>
          </View>
          <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
            <Text style={[styles.label, { color: pal.mute, marginBottom: 8 }]}>Day Sheet Note</Text>
            {notesDiverge ? (
              <Text style={{ color: pal.warn, fontSize: 12.5, marginBottom: 8 }}>
                This note no longer matches planner travel{travelTimeLabel ? ` (${travelTimeLabel})` : ''}.
              </Text>
            ) : null}
            {onPersistNotes ? (
              <TextInput
                value={notesDraft}
                onChangeText={(t) => {
                  notesBoot.current = dateKey;
                  setNotesDraft(t);
                  onPersistNotes(t);
                }}
                placeholder="After show — drive time, next city, anything the crew needs tonight"
                placeholderTextColor={pal.faint}
                multiline
                style={{ color: pal.inkSoft, fontSize: 14.5, lineHeight: 21, minHeight: 80, textAlignVertical: 'top' }}
              />
            ) : (
              <Text style={{ color: pal.inkSoft, fontSize: 14.5, lineHeight: 21 }}>{notesText || 'No Day Sheet Note for this day yet.'}</Text>
            )}
          </View>
          <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
              <Text style={[styles.label, { color: pal.mute }]}>Party on site</Text>
              <Text style={[styles.meta, { color: pal.faint }]}>{members.length} in party</Text>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
              {members.slice(0, 24).map((m) => (
                <Text key={m.id || m.name} style={[styles.chip, { color: pal.ink, backgroundColor: pal.page, borderLeftColor: accent || pal.teal }]}>
                  {m.name || m.displayName || 'Member'}
                </Text>
              ))}
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

function AddRiderCategory({ pal, ink, onAdd }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  if (!open) {
    return (
      <TouchableOpacity
        onPress={() => setOpen(true)}
        style={{ marginTop: 10, borderWidth: 1, borderStyle: 'dashed', borderColor: pal.line, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12 }}
      >
        <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 14 }}>Add category</Text>
      </TouchableOpacity>
    );
  }
  return (
    <View style={{ marginTop: 10, gap: 8 }}>
      <TextInput
        value={label}
        onChangeText={setLabel}
        placeholder="Category name"
        placeholderTextColor={pal.faint}
        style={{ color: ink, borderWidth: 1, borderColor: pal.line, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8 }}
      />
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <TouchableOpacity
          onPress={() => {
            const name = String(label || '').trim();
            if (name) onAdd?.(name);
            setLabel('');
            setOpen(false);
          }}
          style={[styles.primaryBtn, { backgroundColor: ink, paddingVertical: 8 }]}
        >
          <Text style={{ color: pal.page, fontWeight: '600', textAlign: 'center' }}>Add</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => { setOpen(false); setLabel(''); }} style={[styles.outlineBtn, { borderColor: pal.line }]}>
          <Text style={{ color: pal.mute, textAlign: 'center' }}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function AddRiderLineButton({ pal, onPress }) {
  if (Platform.OS === 'web') {
    return (
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onPress}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 2,
          padding: '8px 2px',
          border: 'none',
          background: 'transparent',
          color: pal.teal,
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 600,
          fontSize: 15,
        }}
      >
        + Add line
      </button>
    );
  }
  return (
    <TouchableOpacity onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8 }}>
      <Ionicons name="add" size={16} color={pal.teal} />
      <Text style={{ color: pal.teal, fontWeight: '600' }}>Add line</Text>
    </TouchableOpacity>
  );
}

function RiderAreaEditor({ pal, ink, areaId, areasText, onPersist }) {
  const pendingText = useRef('');
  const [categories, setCategories] = useState(() => categoriesForArea(parseRiderAreas(areasText), areaId));
  const canEdit = Boolean(onPersist);

  useEffect(() => {
    const source = pendingText.current && areasText !== pendingText.current
      ? pendingText.current
      : areasText;
    if (pendingText.current && areasText === pendingText.current) pendingText.current = '';
    setCategories(categoriesForArea(parseRiderAreas(source), areaId));
  }, [areasText, areaId]);

  const commit = (nextCategories) => {
    const base = pendingText.current || areasText;
    const text = serializeRiderAreas(withAreaCategories(parseRiderAreas(base), areaId, nextCategories));
    pendingText.current = text;
    setCategories(nextCategories);
    onPersist?.(text);
  };
  const lineStyle = {
    flex: 1,
    color: ink,
    borderWidth: 1,
    borderColor: pal.line,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 15,
    minHeight: 40,
  };
  return (
    <View style={{ gap: 16 }}>
      {categories.map((category) => (
        <View key={category.id} style={{ gap: 8 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <Text style={[styles.label, { color: ink }]}>{category.label}</Text>
            {canEdit ? (
              <TouchableOpacity
                onPress={() => commit(categories.filter((row) => row.id !== category.id))}
                accessibilityLabel={`Remove ${category.label}`}
              >
                <Text style={{ color: pal.warn || '#c0483c', fontWeight: '700', fontSize: 13 }}>Remove</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {(category.lines || ['']).map((line, index) => (
            <View key={`${category.id}-${index}`} style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              {canEdit ? (
                <TextInput
                  value={line}
                  onChangeText={(text) => {
                    const lines = [...(category.lines || [''])];
                    lines[index] = text;
                    commit(categories.map((row) => (row.id === category.id ? { ...row, lines } : row)));
                  }}
                  onSubmitEditing={() => commit(addLineToCategory(categories, category.id))}
                  placeholder="Add a line"
                  placeholderTextColor={pal.faint}
                  accessibilityLabel={`${category.label} line ${index + 1}`}
                  style={lineStyle}
                />
              ) : (
                <Text style={{ color: ink, fontSize: 15, flex: 1 }}>{line || '—'}</Text>
              )}
              {canEdit ? (
                <TouchableOpacity
                  onPress={() => {
                    const lines = (category.lines || []).filter((_, i) => i !== index);
                    commit(categories.map((row) => (row.id === category.id ? { ...row, lines: lines.length ? lines : [''] } : row)));
                  }}
                  accessibilityLabel={`Remove line ${index + 1}`}
                >
                  <Ionicons name="close" size={16} color={pal.mute} />
                </TouchableOpacity>
              ) : null}
            </View>
          ))}
          {canEdit ? (
            <AddRiderLineButton
              pal={pal}
              onPress={() => commit(addLineToCategory(categories, category.id))}
            />
          ) : null}
        </View>
      ))}
      {canEdit ? (
        <AddRiderCategory
          pal={pal}
          ink={ink}
          onAdd={(label) => commit([...categories, { id: `cat_${Date.now()}`, label, lines: [''] }])}
        />
      ) : null}
    </View>
  );
}

export function MasterTourRiders({
  tourName,
  tour,
  folderId,
  demoTour = false,
  riderFiles,
  isDarkMode,
  onUpload,
  actColors = {},
  onChangeActColor,
  ownerId,
  ownerName,
  dateKey,
  partyMembers = [],
  riderHtml = '',
  areasText = '',
  onPersistAreas,
  onToast,
}) {
  const pal = palette(isDarkMode);
  const { theme } = useTheme();
  const [sel, setSel] = useState('combined');
  const [pkg, setPkg] = useState(() => ({ ...DEFAULT_RIDER_PACKAGE }));
  const [sent, setSent] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const library = useMemo(
    () =>
      buildRiderLibrary({
        tour,
        tourName,
        folderId,
        demoTour,
        riderFileCount: (riderFiles || []).length,
      }),
    [tour, tourName, folderId, demoTour, riderFiles]
  );
  useEffect(() => {
    if (!library.some((a) => a.id === sel)) setSel('combined');
  }, [library, sel]);
  useEffect(() => {
    setSent(false);
  }, [dateKey, folderId]);
  const selected = library.find((a) => a.id === sel) || library[0];
  const hue = riderLibraryColor(selected, isDarkMode, actColors);
  const ink = pal.ink;
  const writerColor = hue || ink;
  const writerBorder = hue || pal.line;
  const onCount = RIDER_PACKAGE_SECTIONS.filter((p) => pkg[p.key]).length;
  const dateLabel = formatDateLong(dateKey);

  const openSend = () => {
    if (!ownerId || !folderId || !dateKey) {
      onToast?.('Select a tour and day before sending.', 'error');
      return;
    }
    setEmailOpen(true);
  };

  const handleSend = async (queue, note) => {
    if (!queue?.length) {
      onToast?.('Add at least one recipient.', 'error');
      return;
    }
    try {
      setSending(true);
      const db = getFirestoreDb();
      const modules = shareModulesForRiderPackage(pkg);
      const share = await upsertDayShareLink(db, ownerId, {
        tourId: folderId,
        tourName,
        dateKey,
        modules,
        baseUrl: resolveWebBaseUrl(),
        visibility: SHARE_VISIBILITY.LINK,
        label: `Advance · ${dateKey}`,
      });
      await sendRiderToEmails({
        db,
        ownerId,
        ownerName,
        recipients: queue,
        tourId: folderId,
        tourName,
        dateKey,
        dateLabel,
        tabKey: 'RIDER_DAILY',
        riderHtml,
        shareLink: share.url,
        message: riderPackageMessage(pkg, note),
      });
      setEmailOpen(false);
      setSent(true);
      onToast?.(
        `Advance sent to ${queue.length} recipient${queue.length === 1 ? '' : 's'}.`,
        'success'
      );
    } catch (err) {
      onToast?.(err?.message || 'Could not send rider email.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={{ flex: 1, flexDirection: 'row', minHeight: 0, backgroundColor: pal.page }}>
      <ScrollView
        style={{ flex: 1, minWidth: 0, borderRightWidth: 1, borderRightColor: pal.line, backgroundColor: pal.surf }}
        contentContainerStyle={{ padding: 20 }}
      >
        <Text style={[styles.label, { color: pal.mute, marginBottom: 12 }]}>Rider library</Text>
        {library.map((a) => {
          const actHue = riderLibraryColor(a, isDarkMode, actColors);
          const textColor = actHue || ink;
          const active = sel === a.id;
          const isCombined = a.id === 'combined';
          const isVehicle = a.id === RIDER_VEHICLE_ID;
          return (
            <TouchableOpacity
              key={a.id}
              onPress={() => setSel(a.id)}
              style={[
                styles.libraryCard,
                {
                  borderColor: actHue || pal.line,
                  borderLeftWidth: isCombined ? 1 : 4,
                  backgroundColor: active
                    ? (actHue ? hexWithAlpha(actHue, isDarkMode ? 0.22 : 0.12) : pal.page)
                    : pal.surf,
                  marginTop: isVehicle ? 14 : 0,
                },
              ]}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontWeight: '600', color: textColor, flex: 1 }}>{a.name}</Text>
                {!isCombined && onChangeActColor ? (
                  <WebColorInput
                    value={actHue || (isVehicle ? RIDER_VEHICLE_COLOR.light : RIDER_BAND_COLORS[0].light)}
                    onChange={(hex) => onChangeActColor(a.id, hex)}
                    pal={pal}
                    ariaLabel={`Color for ${a.name}`}
                    title="Change this rider’s color"
                    width={22}
                    height={18}
                  />
                ) : null}
                {a.pages ? <Text style={[styles.meta, { color: textColor }]}>{a.pages}</Text> : null}
              </View>
              <Text style={{ color: textColor, fontSize: 14.5, marginTop: 2, opacity: isCombined ? 1 : 0.8 }}>{a.role}</Text>
            </TouchableOpacity>
          );
        })}
        <View style={{ height: 1, backgroundColor: pal.line, marginVertical: 20 }} />
        <Text style={[styles.label, { color: pal.mute, marginBottom: 10 }]}>Combined advance package</Text>
        {RIDER_PACKAGE_SECTIONS.map((item) => (
          <TouchableOpacity
            key={item.key}
            onPress={() => { setPkg((p) => ({ ...p, [item.key]: !p[item.key] })); setSent(false); }}
            style={{ flexDirection: 'row', gap: 10, paddingVertical: 8, alignItems: 'flex-start' }}
          >
            <View style={[styles.box, { backgroundColor: pkg[item.key] ? ink : pal.surf, borderColor: ink }]}>
              <Text style={{ color: pal.page, fontSize: 10 }}>{pkg[item.key] ? '✓' : ''}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ color: ink, fontSize: 14.5 }}>{item.label}</Text>
              <Text style={{ color: pal.mute, fontSize: 11 }}>{item.meta}</Text>
            </View>
          </TouchableOpacity>
        ))}
        {(pkg.extra || []).map((item, i) => (
          <TouchableOpacity
            key={item.key || i}
            onPress={() => {
              setPkg((p) => ({
                ...p,
                extra: (p.extra || []).map((row, j) => (j === i ? { ...row, on: !row.on } : row)),
              }));
              setSent(false);
            }}
            style={{ flexDirection: 'row', gap: 10, paddingVertical: 8, alignItems: 'flex-start' }}
          >
            <View style={[styles.box, { backgroundColor: item.on ? ink : pal.surf, borderColor: ink }]}>
              <Text style={{ color: pal.page, fontSize: 10 }}>{item.on ? '✓' : ''}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ color: ink, fontSize: 14.5 }}>{item.label}</Text>
              <Text style={{ color: pal.mute, fontSize: 11 }}>Custom category</Text>
            </View>
          </TouchableOpacity>
        ))}
        <AddRiderCategory pal={pal} ink={ink} onAdd={(label) => {
          const key = `extra-${Date.now()}`;
          setPkg((p) => ({ ...p, extra: [...(p.extra || []), { key, label, on: true }] }));
        }} />
        <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 12, marginBottom: 12 }}>
          {onCount} of {RIDER_PACKAGE_SECTIONS.length} sections · live share link, sent once from this page.
        </Text>
        <TouchableOpacity
          onPress={openSend}
          disabled={sending}
          style={[styles.primaryBtn, { backgroundColor: sent ? pal.sunk : ink, opacity: sending ? 0.7 : 1 }]}
        >
          {sending ? (
            <ActivityIndicator color={pal.page} />
          ) : (
            <Text style={{ color: sent ? pal.ink : pal.page, fontWeight: '600', textAlign: 'center' }}>
              {sent ? 'Advance sent ✓' : 'Send combined package'}
            </Text>
          )}
        </TouchableOpacity>
        {onUpload ? (
          <TouchableOpacity
            onPress={onUpload}
            style={[styles.outlineBtn, { borderColor: writerColor, marginTop: 8 }]}
          >
            <Text style={{ color: writerColor, textAlign: 'center' }}>Upload rider file</Text>
          </TouchableOpacity>
        ) : null}
        <Text style={{ color: pal.faint, fontSize: 11, marginTop: 8, lineHeight: 16 }}>
          Emails a live advance link for this day. We do not invent a merged PDF or a venue inbox.
        </Text>
      </ScrollView>
      <ScrollView
        style={{ flex: 2, minWidth: 0 }}
        contentContainerStyle={{ padding: 24 }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <View>
            <Text style={[styles.label, { color: writerColor, marginBottom: 6 }]}>{selected?.role || 'Rider'}</Text>
            <Text style={[styles.heroSm, { color: writerColor }]}>
              {selected?.id === 'combined' ? 'Combined rider — all acts' : (selected?.name || tourName || 'Rider')}
            </Text>
            <Text style={{ color: pal.mute, marginTop: 6, maxWidth: 520 }}>
              {selected?.id === 'combined'
                ? 'One document, sent once. Add lines and categories here before the venue sees them.'
                : selected?.id === RIDER_VEHICLE_ID
                  ? 'Bus, trailer, load-in, and parking for this tour. Add a line under each category, or add another category.'
                  : `${selected?.name || 'This act'}’s rider. Add a line or a category directly in this color.`}
            </Text>
          </View>
        </View>
        {(riderFiles || []).length ? riderFiles.map((f) => (
          <View key={f.id} style={[styles.card, { backgroundColor: pal.surf, borderColor: writerBorder, marginBottom: 16, padding: 0, overflow: 'hidden' }]}>
            <Text style={[styles.label, { color: writerColor, paddingHorizontal: 18, paddingTop: 12 }]}>Attachment</Text>
            <View style={{ flexDirection: 'row', gap: 14, paddingHorizontal: 18, paddingVertical: 11, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
              <Text style={[styles.meta, { color: writerColor, width: 52 }]}>FILE</Text>
              <Text style={{ flex: 1, color: writerColor, fontSize: 14.5 }}>{f.name || 'Rider file'}</Text>
              <Text style={[styles.meta, { color: pal.faint }]}>{f.fileType || ''}</Text>
            </View>
          </View>
        )) : null}
        <View style={[styles.card, { backgroundColor: pal.surf, borderColor: writerBorder, padding: 18, marginTop: (riderFiles || []).length ? 0 : 0 }]}>
          <RiderAreaEditor
            pal={pal}
            ink={writerColor}
            areaId={selected?.id || 'combined'}
            areasText={areasText}
            onPersist={onPersistAreas}
          />
        </View>
      </ScrollView>
      <RiderEmailRecipientsModal
        visible={emailOpen}
        onClose={() => !sending && setEmailOpen(false)}
        onSend={(queue, note) => void handleSend(queue, note)}
        sending={sending}
        ownerId={ownerId}
        tourId={folderId}
        selectedDateKey={dateKey}
        tourPartyMembers={partyMembers}
        theme={theme}
      />
    </View>
  );
}

export { MasterTourContacts } from './MasterTourOpsViews';

function playChatChime() {
  if (typeof window === 'undefined') return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.07);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.09, ctx.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.22);
    osc.onended = () => ctx.close().catch(() => {});
  } catch {
    /* autoplay / unsupported */
  }
}

export function MasterTourChat({
  partyMembers,
  isDarkMode,
  accent,
  tourName,
  chatText = '',
  currentUserName = 'You',
  currentUserId = '',
  currentUserPhotoURL = '',
  onPersist,
  onInvite,
  onNotifyNewMessage,
  chatColors,
  canManageParty = false,
}) {
  const pal = palette(isDarkMode);
  const { width } = useWindowDimensions();
  const compact = width < 860;
  const colors = {
    accent: chatColors?.accent || accent || pal.teal,
    bubble: chatColors?.bubble || pal.surf,
    mine: chatColors?.mine || accent || pal.teal,
    channel: chatColors?.channel || pal.inkSoft,
  };
  const roster = partyMembers || [];
  const [chan, setChan] = useState('party');
  const [draft, setDraft] = useState('');
  const [rosterOpen, setRosterOpen] = useState(false);
  const [state, setState] = useState(() => parseChatState(chatText));
  const seenMessageIdsRef = useRef(collectChatMessageIds(parseChatState(chatText).channels));
  const threadRef = useRef(null);
  useEffect(() => {
    const incoming = parseChatState(chatText);
    if (shouldPlayChatChime(seenMessageIdsRef.current, incoming.channels, currentUserId, currentUserName)) {
      playChatChime();
    }
    seenMessageIdsRef.current = collectChatMessageIds(incoming.channels);
    setState((prev) => ({
      ...incoming,
      channels: preferLongerChatChannels(prev.channels, incoming.channels),
      reads: mergeChatReads(prev.reads, incoming.reads),
    }));
  }, [chatText, currentUserId, currentUserName]);

  const persistState = (next) => {
    const latest = parseChatState(chatText);
    const merged = {
      channels: preferLongerChatChannels(latest.channels, next.channels),
      members: canManageParty ? next.members : latest.members,
      channelColors: canManageParty ? next.channelColors : latest.channelColors,
      reads: mergeChatReads(latest.reads, next.reads),
    };
    setState(merged);
    onPersist?.(serializeChatState(merged));
  };

  const visibleChannels = useMemo(() => {
    const me = roster.find((m) => m.id === currentUserId) || { id: currentUserId };
    return CHAT_CHANNEL_META.filter((ch) => canManageParty || isMemberOnChannel(state.members, ch.id, me));
  }, [state.members, roster, currentUserId, canManageParty]);

  useEffect(() => {
    if (!visibleChannels.some((c) => c.id === chan) && visibleChannels[0]) {
      setChan(visibleChannels[0].id);
    }
  }, [chan, visibleChannels]);

  const channelPeople = membersForChannel(state.members, chan, roster);
  const channelKeys = new Set(channelPeople.map(chatMemberKey).filter(Boolean));
  const outsidePeople = roster.filter((m) => !channelKeys.has(chatMemberKey(m)));
  const channelHue = state.channelColors?.[chan] || colors.channel;
  const messages = state.channels[chan] || [];

  useEffect(() => {
    if (!currentUserId || !messages.length) return;
    const latestAt = messages[messages.length - 1]?.at;
    if (!latestAt) return;
    const already = state.reads?.[chan]?.[currentUserId];
    if (already && Date.parse(already) >= Date.parse(latestAt)) return;
    persistState({
      ...state,
      reads: markChannelRead(state.reads, chan, currentUserId, latestAt),
    });
  }, [chan, messages, currentUserId]);

  useEffect(() => {
    const node = threadRef.current;
    if (!node || typeof node.scrollToEnd !== 'function') return;
    node.scrollToEnd({ animated: false });
  }, [messages.length, chan]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const me = roster.find((m) => m.id === currentUserId);
    const msg = {
      id: `m-${Date.now()}`,
      who: currentUserName || 'You',
      uid: currentUserId || '',
      photoURL: String(currentUserPhotoURL || me?.photoURL || '').trim(),
      text,
      at: new Date().toISOString(),
    };
    persistState({
      ...state,
      channels: { ...emptyChatChannels(), ...state.channels, [chan]: [...(state.channels[chan] || []), msg] },
    });
    setDraft('');
    if (typeof onNotifyNewMessage === 'function') {
      try {
        onNotifyNewMessage({
          channelId: chan,
          message: msg,
          members: channelPeople,
          membersMap: state.members,
        });
      } catch (err) {
        console.warn('tour chat notify', err);
      }
    }
  };
  const stamp = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return String(iso);
    }
  };

  const setChannelColor = (id, hex) => {
    persistState({
      ...state,
      channelColors: { ...state.channelColors, [id]: hex },
    });
  };

  const toggleMember = (member) => {
    if (!canManageParty) return;
    persistState({
      ...state,
      members: toggleChatMember(state.members, chan, member, roster),
    });
  };

  const MemberAvatar = ({ member, size = 26, overlap = 0 }) => {
    const source = getUserPhotoSource(
      {
        id: member?.id || member?.uid,
        uid: member?.uid || member?.id,
        email: member?.email,
        photoURL: member?.photoURL,
      },
      false
    );
    return (
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          marginLeft: overlap,
          backgroundColor: pal.sunk,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 1,
          borderColor: pal.surf,
          overflow: 'hidden',
        }}
      >
        {source ? (
          <Image source={source} style={{ width: size, height: size }} />
        ) : (
          <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: Math.max(9, size * 0.34), fontWeight: '600' }}>
            {initialsOf(
              displayNameFromUserLike(member),
              member?.email || member?.id || member?.uid
            )}
          </Text>
        )}
      </View>
    );
  };

  const rosterPanel = rosterOpen ? (
    <View
      style={{
        width: compact ? '100%' : 280,
        maxWidth: 320,
        borderLeftWidth: compact ? 0 : 1,
        borderLeftColor: pal.line,
        backgroundColor: pal.surf,
        ...(compact
          ? { position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 8, borderLeftWidth: 1, borderLeftColor: pal.line }
          : {}),
      }}
    >
      <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: pal.line, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.label, { color: pal.mute }]}>In this channel</Text>
          <Text style={{ color: pal.ink, fontWeight: '600', marginTop: 2 }}>
            {CHAT_CHANNEL_META.find((c) => c.id === chan)?.name} · {channelPeople.length}
          </Text>
        </View>
        <TouchableOpacity onPress={() => setRosterOpen(false)} accessibilityLabel="Close channel members">
          <Ionicons name="close" size={22} color={pal.ink} />
        </TouchableOpacity>
      </View>
      <ScrollView contentContainerStyle={{ padding: 12, paddingBottom: 28, gap: 6 }}>
        {channelPeople.length ? channelPeople.map((m) => (
          <View key={m.id || m.email} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
            <MemberAvatar member={m} size={32} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: pal.ink, fontWeight: '600' }} numberOfLines={1}>{m.name}</Text>
              <Text style={[styles.meta, { color: pal.mute }]} numberOfLines={1}>{m.role || m.email || 'Tour party'}</Text>
            </View>
            {canManageParty ? (
              <TouchableOpacity onPress={() => toggleMember(m)} style={[styles.outlineBtn, { paddingVertical: 6, paddingHorizontal: 10, borderColor: pal.line }]}>
                <Text style={{ color: pal.ink, fontSize: 12 }}>Remove</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        )) : (
          <Text style={{ color: pal.mute, paddingVertical: 8 }}>Nobody is on this channel yet.</Text>
        )}
        {canManageParty && outsidePeople.length ? (
          <>
            <Text style={[styles.label, { color: pal.mute, marginTop: 14, marginBottom: 6 }]}>Add from party</Text>
            {outsidePeople.map((m) => (
              <View key={m.id || m.email} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
                <MemberAvatar member={m} size={32} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ color: pal.ink, fontWeight: '500' }} numberOfLines={1}>{m.name}</Text>
                  <Text style={[styles.meta, { color: pal.mute }]} numberOfLines={1}>{m.role || m.email || 'Tour party'}</Text>
                </View>
                <TouchableOpacity onPress={() => toggleMember(m)} style={[styles.primaryBtn, { backgroundColor: colors.accent, paddingVertical: 6, paddingHorizontal: 10 }]}>
                  <Text style={{ color: '#fffffe', fontSize: 12, fontWeight: '600' }}>Add</Text>
                </TouchableOpacity>
              </View>
            ))}
          </>
        ) : null}
        {!canManageParty ? (
          <Text style={{ color: pal.mute, fontSize: 12.5, lineHeight: 17, marginTop: 12 }}>
            A tour manager can add or remove people from this channel.
          </Text>
        ) : null}
      </ScrollView>
    </View>
  ) : null;

  if (!visibleChannels.length) {
    return (
      <View style={{ flex: 1, backgroundColor: pal.page, padding: 28, justifyContent: 'center', maxWidth: 520 }}>
        <Text style={{ color: pal.ink, fontWeight: '600', fontSize: 18 }}>No channels yet</Text>
        <Text style={{ color: pal.mute, marginTop: 8, lineHeight: 20 }}>
          A tour manager has not added you to any chat channels for this tour.
        </Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: pal.page, position: 'relative' }}>
      <View style={{ width: compact ? 210 : 250, borderRightWidth: 1, borderRightColor: pal.line, backgroundColor: pal.surf, padding: 14 }}>
        <Text style={[styles.label, { color: pal.mute, marginBottom: 12 }]}>Channels</Text>
        {visibleChannels.map((ch) => {
          const hue = state.channelColors?.[ch.id] || colors.channel;
          const on = chan === ch.id;
          const fg = on ? inkOnHex(hue) : pal.ink;
          const count = membersForChannel(state.members, ch.id, roster).length;
          return (
            <View key={ch.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {canManageParty ? (
                <WebColorInput
                  value={hue}
                  onChange={(hex) => setChannelColor(ch.id, hex)}
                  pal={pal}
                  ariaLabel={`${ch.name} color`}
                  title={`Color ${ch.name}`}
                  width={22}
                  height={22}
                />
              ) : (
                <View style={{ width: 10, height: 22, borderRadius: 4, backgroundColor: hue }} />
              )}
              <TouchableOpacity
                onPress={() => setChan(ch.id)}
                style={[
                  styles.chan,
                  { flex: 1, borderLeftWidth: 3, borderLeftColor: hue },
                  on && { backgroundColor: hue },
                ]}
              >
                <Text style={{ color: fg, fontWeight: on ? '600' : '400', flex: 1 }} numberOfLines={1}>{ch.name}</Text>
                <Text style={[styles.meta, { color: on ? fg : pal.mute }]}>{count}</Text>
              </TouchableOpacity>
            </View>
          );
        })}
        <Text style={[styles.label, { color: pal.mute, paddingHorizontal: 6, marginBottom: 8, marginTop: 8 }]}>Invite</Text>
        <Text style={{ color: pal.mute, fontSize: 14.5, lineHeight: 18, paddingHorizontal: 6, marginBottom: 10 }}>
          {canManageParty
            ? 'Adding people works from the party list. They need a VRC account — invite them to sign up, then add them here.'
            : 'Invite from Tour settings. People must sign up before they can join chat.'}
        </Text>
        {onInvite ? (
          <TouchableOpacity
            onPress={onInvite}
            style={[styles.outlineBtn, { borderColor: pal.ink, marginTop: 4 }]}
          >
            <Text style={{ color: pal.ink, textAlign: 'center' }}>
              Invite {roster.length} party
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={{ flex: 1 }}>
        <View style={{ padding: 16, borderBottomWidth: 1, borderBottomColor: pal.line, flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <View style={{ flex: 1 }}>
            <Text style={{ color: pal.ink, fontWeight: '600' }}>{CHAT_CHANNEL_META.find((c) => c.id === chan)?.name}</Text>
            <Text style={[styles.meta, { color: pal.mute }]}>
              {(tourName || 'Tour')} · {channelPeople.length} in this channel
            </Text>
          </View>
          <TouchableOpacity
            onPress={() => setRosterOpen((open) => !open)}
            accessibilityLabel="Show channel members"
            style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingLeft: 8 }}
          >
            <View style={{ flexDirection: 'row' }}>
              {channelPeople.slice(0, 7).map((m, i) => (
                <MemberAvatar key={m.id || m.email || i} member={m} overlap={i ? -6 : 0} />
              ))}
              {!channelPeople.length ? (
                <View
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 13,
                    borderWidth: 1,
                    borderColor: pal.line,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: pal.sunk,
                  }}
                >
                  <Ionicons name="people-outline" size={14} color={pal.mute} />
                </View>
              ) : null}
            </View>
            <Ionicons name={rosterOpen ? 'chevron-forward' : 'chevron-back'} size={16} color={pal.mute} style={{ marginLeft: 6 }} />
          </TouchableOpacity>
        </View>
        <ScrollView ref={threadRef} style={{ flex: 1 }} contentContainerStyle={{ padding: 16, gap: 10 }}>
          {messages.length ? messages.map((m, i) => {
            const mine = isOwnChatMessage(m, currentUserId, currentUserName);
            const member = chatMemberForMessage(m, roster) || {
              id: m.uid,
              name: m.who,
              photoURL: m.photoURL,
            };
            const receipts = readersForMessage(state.reads, chan, m, messages, roster);
            const bubbleBg = mine ? colors.mine : colors.bubble;
            const fg = mine ? pal.page : pal.ink;
            const meta = mine ? pal.page : pal.mute;
            return (
            <View
              key={m.id || `${m.at}-${i}`}
              style={{
                alignSelf: mine ? 'flex-end' : 'flex-start',
                maxWidth: '78%',
                alignItems: mine ? 'flex-end' : 'flex-start',
              }}
            >
              <View style={{ flexDirection: mine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
                {mine ? null : <MemberAvatar member={member} size={28} />}
                <View
                  style={{
                    backgroundColor: bubbleBg,
                    borderRadius: mine ? 18 : 18,
                    borderBottomRightRadius: mine ? 5 : 18,
                    borderBottomLeftRadius: mine ? 18 : 5,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    maxWidth: '100%',
                  }}
                >
                  {mine ? (
                    <Text style={{ color: meta, fontSize: 11, textAlign: 'right' }}>{stamp(m.at)}</Text>
                  ) : (
                    <Text style={{ color: meta, fontSize: 11 }}>{m.who}{m.at ? ` · ${stamp(m.at)}` : ''}</Text>
                  )}
                  <Text style={{ color: fg, marginTop: 2 }}>{m.text}</Text>
                </View>
              </View>
              {receipts.length ? (
                <View style={{ flexDirection: 'row', marginTop: 4, paddingHorizontal: mine ? 0 : 36 }}>
                  {receipts.slice(0, 6).map((reader, ri) => (
                    <MemberAvatar key={reader.id || reader.email || ri} member={reader} size={16} overlap={ri ? -5 : 0} />
                  ))}
                </View>
              ) : null}
            </View>
            );
          }) : (
            <Text style={{ color: pal.mute }}>No messages in this channel yet.</Text>
          )}
        </ScrollView>
        <View style={{ flexDirection: 'row', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: pal.line }}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={send}
            placeholder="Message this channel"
            placeholderTextColor={pal.faint}
            style={[styles.input, { color: pal.ink, borderColor: pal.line, backgroundColor: pal.surf, flex: 1 }]}
          />
          <TouchableOpacity onPress={send} style={[styles.primaryBtn, { backgroundColor: channelHue, paddingHorizontal: 18 }]}>
            <Text style={{ color: inkOnHex(channelHue), fontWeight: '600' }}>Send</Text>
          </TouchableOpacity>
        </View>
      </View>
      {rosterPanel}
    </View>
  );
}

export function MasterTourPhone({ dateKey, card, scheduleText, lodgingSummary, groundLine, isDarkMode, accent, prefs, embedded = false }) {
  const pal = palette(isDarkMode);
  const d = dateKey ? new Date(`${dateKey}T12:00:00`) : new Date();
  const rows = scheduleRowsFromText(scheduleText);
  const next = rows.find((s) => s.key) || rows[0];
  const fmt = (t) => formatClockTime(t, prefs?.timeFormat || '12-hour');
  const phoneInner = pal.page;
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: pal.page }}
      contentContainerStyle={{
        padding: embedded ? 16 : 34,
        flexDirection: embedded ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: embedded ? 16 : 40,
        justifyContent: 'center',
      }}
    >
      <View style={{ width: embedded ? '100%' : 390, maxWidth: 390, alignSelf: 'center', borderRadius: 42, backgroundColor: pal.ink, padding: 11 }}>
        <View style={{ borderRadius: 32, backgroundColor: phoneInner, overflow: 'hidden', minHeight: 640, paddingBottom: 12 }}>
          <View style={{ paddingHorizontal: 20, paddingTop: 12, flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={[styles.mono, { color: pal.ink }]}>9:41</Text>
            <View style={{ width: 15, height: 8, borderWidth: 1, borderColor: pal.ink, borderRadius: 2 }} />
          </View>
          <View style={{ paddingHorizontal: 20, paddingTop: 8, paddingBottom: 14 }}>
            <Text style={[styles.label, { color: pal.mute }]}>{d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</Text>
            <Text style={{ fontSize: 27, fontWeight: '700', color: pal.ink, marginTop: 2 }}>{card?.cityLine || '—'}</Text>
            <Text style={{ color: pal.mute, marginTop: 4 }}>{card?.venueLine || 'Day sheet'}</Text>
          </View>
          <View style={{ paddingHorizontal: 14, gap: 10 }}>
            <View style={{ backgroundColor: pal.ink, borderRadius: 14, padding: 16 }}>
              <Text style={[styles.label, { color: pal.faint }]}>Next up</Text>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 5 }}>
                <Text style={[styles.mono, { color: pal.page, fontSize: 22 }]}>{next ? fmt(next.t) : '—'}</Text>
                <Text style={{ color: pal.page, fontSize: 14, fontWeight: '500' }}>{next?.label || '—'}</Text>
              </View>
            </View>
            <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line, paddingVertical: 8 }]}>
              <Text style={[styles.label, { color: pal.mute, marginBottom: 4 }]}>Schedule</Text>
              {rows.length ? rows.slice(0, 8).map((s, i) => (
                <View key={`${s.t}-${i}`} style={{ flexDirection: 'row', gap: 12, paddingVertical: 8, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
                  <Text style={[styles.mono, { color: pal.ink, width: 50 }]}>{fmt(s.t)}</Text>
                  <Text style={{ color: pal.ink, flex: 1 }}>{s.label}</Text>
                </View>
              )) : <Text style={{ color: pal.mute }}>No schedule rows.</Text>}
            </View>
            <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
              <Text style={[styles.label, { color: pal.mute, marginBottom: 6 }]}>Hotel</Text>
              <Text style={{ color: pal.ink, fontWeight: '500' }}>{lodgingSummary || '—'}</Text>
            </View>
            <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
              <Text style={[styles.label, { color: pal.mute, marginBottom: 6 }]}>Ground</Text>
              <Text style={{ color: pal.ink, fontWeight: '500' }}>{groundLine || '—'}</Text>
            </View>
          </View>
        </View>
      </View>
      {embedded ? null : (
      <View style={{ maxWidth: 300, paddingTop: 20 }}>
        <Text style={[styles.label, { color: pal.mute, marginBottom: 10 }]}>Crew view</Text>
        <Text style={{ color: pal.inkSoft, fontSize: 15, lineHeight: 21 }}>
          The phone sheet shows one day and nothing else: next call time, the schedule, where you sleep, and what picks you up. Per diems and settlement stay off unless the reader is a tour manager or accountant.
        </Text>
      </View>
      )}
    </ScrollView>
  );
}

export function MasterTourDaySheetWithPhone(props) {
  const { width } = useWindowDimensions();
  const pal = palette(props.isDarkMode);
  const showPhone = width >= 1080;
  const card = pickTourDayCard(props.tourDayMetaByDate?.[props.dateKey], props.selectedTourFilterId);
  return (
    <View style={{ flex: 1, flexDirection: showPhone ? 'row' : 'column', minHeight: 0, backgroundColor: pal.page }}>
      <View style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <MasterTourDaySheet {...props} />
      </View>
      {showPhone ? (
        <View style={{ width: 420, maxWidth: '40%', borderLeftWidth: 1, borderLeftColor: pal.line, flexShrink: 0, minHeight: 0 }}>
          <MasterTourPhone
            dateKey={props.dateKey}
            card={card}
            scheduleText={props.scheduleText}
            lodgingSummary={props.lodgingSummary}
            groundLine={props.groundLine}
            isDarkMode={props.isDarkMode}
            accent={props.accent}
            prefs={props.prefs}
            embedded
          />
        </View>
      ) : null}
    </View>
  );
}

export function MasterTourMap(props) {
  const tourId = String(props.tourId || '').trim();
  if (Platform.OS === 'web' && tourId && props.navigation) {
    return (
      <View style={{ flex: 1, minHeight: 400, width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
        <TourPlanner
          navigation={{
            navigate: (...args) => props.navigation.navigate?.(...args),
            goBack: () => props.navigation.goBack?.(),
            setParams: () => {},
          }}
          route={{
            params: {
              loadTourId: tourId,
              calendarEmbed: true,
              calendarDateKey: String(props.selectedDateKey || '').trim(),
              routeLayerAccent: props.tourAccent || props.accent || null,
            },
          }}
        />
      </View>
    );
  }
  return <MasterTourRoutingMap {...props} />;
}

const barStyles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  wrapSheet: {
    flexDirection: 'column',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    paddingHorizontal: 0,
    paddingVertical: 0,
    gap: 22,
  },
  section: {
    gap: 10,
  },
  sectionTitle: {
    fontFamily: FONT.sans,
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: -0.2,
  },
  field: {
    gap: 6,
  },
  kicker: { fontFamily: FONT.mono, fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' },
  select: { flexDirection: 'row', borderWidth: 1, borderRadius: 8, overflow: 'hidden', alignSelf: 'flex-start' },
  selectOpt: { paddingHorizontal: 12, paddingVertical: 7 },
  selectText: { fontFamily: FONT.sans, fontSize: 13, fontWeight: '600' },
  toggleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  toggle: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1 },
  toggleLabel: { fontFamily: FONT.sans, fontSize: 13, fontWeight: '600' },
  swatches: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  swatch: { width: 16, height: 16, borderRadius: 8, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  swatchLg: { width: 28, height: 28, borderRadius: 14, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  check: { color: '#fffffe', fontSize: 12, fontWeight: '700' },
  colorRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  colorLabel: { fontFamily: FONT.sans, fontSize: 14, fontWeight: '600', flex: 1 },
  colorHex: { fontFamily: FONT.mono, fontSize: 12 },
  sheetActions: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 4 },
  btn: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
  btnPrimary: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
  btnText: { fontSize: 13, fontWeight: '700' },
});

const styles = StyleSheet.create({
  label: { fontFamily: FONT.mono, fontSize: 11.5, letterSpacing: 1.2, textTransform: 'uppercase' },
  meta: { fontFamily: FONT.mono, fontSize: 11.5 },
  mono: { fontFamily: FONT.mono, fontSize: 14, fontWeight: '500' },
  tick: { fontFamily: FONT.mono, fontSize: 10 },
  hero: { fontSize: 36, fontWeight: '700', letterSpacing: -0.6 },
  heroSm: { fontSize: 24, fontWeight: '700', letterSpacing: -0.4 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18 },
  schedRow: { flexDirection: 'row', gap: 14, paddingVertical: 9, borderTopWidth: 1, alignItems: 'baseline' },
  chip: { fontSize: 12.5, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 5, borderLeftWidth: 2, overflow: 'hidden' },
  ganttTrack: { height: 48, borderWidth: 1, borderRadius: 10, position: 'relative' },
  libraryCard: { padding: 11, borderRadius: 10, borderWidth: 1, marginBottom: 6 },
  box: { width: 16, height: 16, borderRadius: 4, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  primaryBtn: { paddingVertical: 11, paddingHorizontal: 14, borderRadius: 9 },
  outlineBtn: { paddingVertical: 10, paddingHorizontal: 15, borderRadius: 8, borderWidth: 1 },
  chan: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8 },
  input: { borderWidth: 1, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14.5 },
  phone: { width: 320, minHeight: 560, borderWidth: 3, borderRadius: 36, padding: 22 },
});

const weekBarStyles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingTop: DAY_STRIP_WRAP_PAD_Y,
    paddingBottom: DAY_STRIP_WRAP_PAD_Y,
    borderBottomWidth: 1,
    flexGrow: 0,
    flexShrink: 0,
    overflow: 'visible',
  },
  wrapFlush: {
    paddingTop: DAY_STRIP_WRAP_PAD_Y,
    borderTopWidth: 0,
    borderBottomWidth: 0,
  },
  scroll: {
    flex: 1,
    minWidth: 0,
    height: DAY_STRIP_ROW_HEIGHT,
    ...(Platform.OS === 'web' ? {
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    } : {}),
  },
  scroller: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DAY_STRIP_GAP,
    paddingRight: 4,
    height: DAY_STRIP_ROW_HEIGHT,
  },
  arrow: {
    width: 32,
    height: DAY_STRIP_ROW_HEIGHT,
    minHeight: DAY_STRIP_ROW_HEIGHT,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
  },
  arrowGlyph: {
    fontFamily: FONT.sans,
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 24,
    marginTop: -2,
  },
  cell: {
    width: DAY_STRIP_CELL,
    flexGrow: 0,
    flexShrink: 0,
    height: DAY_STRIP_ROW_HEIGHT,
    minHeight: DAY_STRIP_ROW_HEIGHT,
    maxHeight: DAY_STRIP_ROW_HEIGHT,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 3,
    justifyContent: 'center',
    overflow: 'visible',
    ...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
  },
  cellIdle: {
    opacity: 0.72,
  },
  cellSelected: {
    opacity: 1,
    zIndex: 2,
  },
  cellTop: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  dow: {
    fontFamily: FONT.mono,
    fontSize: 10.5,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  num: {
    fontFamily: FONT.sans,
    fontSize: 16.5,
    fontWeight: '400',
    letterSpacing: -0.3,
    lineHeight: 20,
  },
  city: {
    fontFamily: FONT.sans,
    fontSize: 13,
    fontWeight: '400',
  },
  kind: {
    fontFamily: FONT.mono,
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  monthRule: {
    width: DAY_STRIP_MONTH_RULE,
    height: DAY_STRIP_ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    flexGrow: 0,
    flexShrink: 0,
    gap: 3,
    paddingVertical: 0,
    paddingRight: 1,
  },
  monthRuleLine: {
    width: 2,
    alignSelf: 'stretch',
    borderRadius: 1,
  },
  monthRuleLetters: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingVertical: 1,
  },
  monthRuleGlyph: {
    fontFamily: FONT.mono,
    fontSize: 13.5,
    fontWeight: '500',
    lineHeight: 15,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  todayBtn: {
    flexShrink: 0,
    height: 36,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  todayBtnText: {
    fontFamily: FONT.sans,
    fontSize: 12.5,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
});
